import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Info, Library } from 'lucide-react';
import { Task } from '@/app/task-bank/public-actions';
import type { User } from 'firebase/auth';

interface TaskBankIdeasButtonsProps {
  user: User | null | undefined;
  selectedTask: Task | null;
  prompt: string;
  showIdeas: boolean;
  setShowIdeas: (v: boolean) => void;
  setTaskBankOpen: (v: boolean) => void;
}

// Add type guard for new Task 1 ideas
const isTask1Ideas = (
  ideas: unknown
): ideas is {
  significantPoints: string[];
  keyComparisons: string[];
  detailParagraphSuggestions: { suggestion: string; logic: string }[];
  usefulVocabulary?: Array<{
    term: string;
    category?: string;
    definition_or_usage?: string;
    example_sentence?: string;
  }>;
} => {
  if (!ideas || typeof ideas !== 'object') return false;
  const obj = ideas as Record<string, unknown>;
  if (
    !Array.isArray(obj.significantPoints) ||
    !Array.isArray(obj.keyComparisons) ||
    !Array.isArray(obj.detailParagraphSuggestions)
  )
    return false;
  // Check detailParagraphSuggestions structure
  if (
    obj.detailParagraphSuggestions.length > 0 &&
    typeof obj.detailParagraphSuggestions[0] !== 'object'
  ) {
    return false;
  }
  // Optionally check usefulVocabulary structure
  if (
    obj.usefulVocabulary !== undefined &&
    (!Array.isArray(obj.usefulVocabulary) ||
      (obj.usefulVocabulary.length > 0 &&
        typeof obj.usefulVocabulary[0] !== 'object'))
  ) {
    return false;
  }
  return true;
};

export function TaskBankIdeasButtons({
  user,
  selectedTask,
  prompt,
  showIdeas,
  setShowIdeas,
  setTaskBankOpen,
}: TaskBankIdeasButtonsProps) {
  return (
    <div className='flex flex-col sm:flex-row gap-2 w-full'>
      <Button
        type='button'
        variant='outline'
        size='sm'
        className='w-full sm:w-auto'
        onClick={() => setTaskBankOpen(true)}
        disabled={!user}
      >
        <Library className='h-4 w-4' />
        Task Bank
      </Button>
      {selectedTask && prompt === selectedTask.prompt && (
        <Popover open={showIdeas} onOpenChange={setShowIdeas}>
          <PopoverTrigger asChild>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='w-full sm:w-auto flex items-center justify-center'
            >
              <Info className='h-4 w-4' />
              Ideas
            </Button>
          </PopoverTrigger>
          <PopoverContent className='min-w-xl'>
            <div className='mb-2 font-medium text-sm'>Ideas for this task</div>
            <div className='max-h-96 overflow-y-auto space-y-3 pr-1'>
              {selectedTask.taskType === 'task1' &&
              selectedTask.task1Ideas &&
              isTask1Ideas(selectedTask.task1Ideas) ? (
                <div className='space-y-2'>
                  <div>
                    <div className='text-sm font-medium mb-1.5 underline decoration-dotted underline-offset-2'>
                      Significant Points
                    </div>
                    <ul className='list-disc list-outside space-y-1 pl-4'>
                      {selectedTask.task1Ideas.significantPoints.map(
                        (point, i) => (
                          <li
                            key={`sig-point-${i}`}
                            className='text-sm text-muted-foreground'
                          >
                            {point}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                  <div>
                    <div className='text-sm font-medium mb-1.5 underline decoration-dotted underline-offset-2'>
                      Key Comparisons
                    </div>
                    <ul className='list-disc list-outside space-y-1 pl-4'>
                      {selectedTask.task1Ideas.keyComparisons.map((comp, i) => (
                        <li
                          key={`key-comp-${i}`}
                          className='text-sm text-muted-foreground'
                        >
                          {comp}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className='text-sm font-medium mb-1.5 underline decoration-dotted underline-offset-2'>
                      Detail Paragraph Suggestions
                    </div>
                    <ul className='list-disc list-outside space-y-1 pl-4'>
                      {selectedTask.task1Ideas.detailParagraphSuggestions.map(
                        (sugg, i) => (
                          <li
                            key={`detail-sugg-${i}`}
                            className='text-sm text-muted-foreground'
                          >
                            {sugg.suggestion}{' '}
                            <span className='italic text-muted-foreground'>
                              ({sugg.logic})
                            </span>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                  {Array.isArray(selectedTask.task1Ideas.usefulVocabulary) &&
                    selectedTask.task1Ideas.usefulVocabulary.length > 0 && (
                      <div>
                        <div className='text-sm font-medium mb-1.5 underline decoration-dotted underline-offset-2'>
                          Useful Vocabulary
                        </div>
                        <ul className='list-disc list-outside space-y-1 pl-4'>
                          {selectedTask.task1Ideas.usefulVocabulary.map(
                            (vocab, i) => (
                              <li
                                key={`vocab-${i}`}
                                className='text-sm text-muted-foreground'
                              >
                                <span className='font-semibold text-foreground'>
                                  {vocab.term}
                                </span>
                                {vocab.category && (
                                  <span className='ml-2 text-xs text-muted-foreground'>
                                    ({vocab.category})
                                  </span>
                                )}
                                {vocab.definition_or_usage && (
                                  <div className='text-xs text-muted-foreground ml-2'>
                                    {vocab.definition_or_usage}
                                  </div>
                                )}
                                {vocab.example_sentence && (
                                  <div className='text-xs italic text-muted-foreground ml-2'>
                                    &quot;{vocab.example_sentence}&quot;
                                  </div>
                                )}
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                </div>
              ) : selectedTask.taskType === 'task2' &&
                (selectedTask.ideas ?? []).length > 0 ? (
                (selectedTask.ideas ?? []).map((ideaGroup, groupIndex) => (
                  <div key={groupIndex}>
                    <p className='text-sm font-medium mb-1.5 underline decoration-dotted underline-offset-2'>
                      {ideaGroup.category}
                    </p>
                    <ul className='list-disc list-outside space-y-1 pl-4'>
                      {ideaGroup.points.map((point, pointIndex) => (
                        <li
                          key={pointIndex}
                          className='text-sm text-muted-foreground'
                        >
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              ) : (
                <p className='text-xs text-muted-foreground italic text-center py-2'>
                  No ideas found for this task.
                </p>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
